import clsx from 'clsx';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';

export type POSStatus = 'connected' | 'pending' | 'error' | 'disconnected' | 'none';

interface POSStatusBadgeProps {
  status: POSStatus;
  posName?: string;
  lastSyncAt?: string | null;
  compact?: boolean;
}

const STATUS_CONFIG: Record<POSStatus, {
  label: string;
  bgColor: string;
  textColor: string;
  icon: React.ComponentType<{ className?: string }>;
  pulse?: boolean;
}> = {
  connected: {
    label: 'Connected',
    bgColor: 'bg-emerald-50',
    textColor: 'text-emerald-700',
    icon: CheckCircleIcon,
  },
  pending: {
    label: 'Connecting...',
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-700',
    icon: ArrowPathIcon,
    pulse: true,
  },
  error: {
    label: 'Connection Error',
    bgColor: 'bg-red-50',
    textColor: 'text-red-700',
    icon: ExclamationCircleIcon,
  },
  disconnected: {
    label: 'Disconnected',
    bgColor: 'bg-slate-100',
    textColor: 'text-slate-600',
    icon: LinkIcon,
  },
  none: {
    label: 'Not Connected',
    bgColor: 'bg-slate-50',
    textColor: 'text-slate-500',
    icon: LinkIcon,
  },
};

export function POSStatusBadge({ status, posName, lastSyncAt, compact = false }: POSStatusBadgeProps) {
  const config = STATUS_CONFIG[status];
  const Icon = config.icon;

  if (compact) {
    return (
      <div
        className={clsx(
          'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
          config.bgColor,
          config.textColor
        )}
        title={posName ? `${posName}: ${config.label}` : config.label}
      >
        <Icon className={clsx('w-3 h-3', config.pulse && 'animate-spin')} />
        {posName || config.label}
      </div>
    );
  }

  return (
    <div
      className={clsx(
        'inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm',
        config.bgColor,
        config.textColor
      )}
    >
      <Icon className={clsx('w-4 h-4', config.pulse && 'animate-spin')} />
      <div className="flex flex-col">
        <span className="font-medium">
          {posName ? `${posName} ${config.label}` : config.label}
        </span>
        {lastSyncAt && status === 'connected' && (
          <span className="text-xs opacity-75">
            Last sync: {formatTimeAgo(lastSyncAt)}
          </span>
        )}
      </div>
    </div>
  );
}

function formatTimeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

// Connection prompt component for stores without POS
interface ConnectPOSPromptProps {
  onConnect: () => void;
}

export function ConnectPOSPrompt({ onConnect }: ConnectPOSPromptProps) {
  return (
    <button
      onClick={onConnect}
      className="w-full flex items-center gap-3 p-3 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-100 rounded-xl text-sm text-amber-700 hover:from-amber-100 hover:to-orange-100 transition-colors group"
    >
      <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center group-hover:bg-amber-200 transition-colors">
        <LinkIcon className="w-4 h-4" />
      </div>
      <div className="flex-1 text-left">
        <div className="font-medium">Connect POS for Auto-Import</div>
        <div className="text-xs text-amber-600">Toast, Square, Clover & more</div>
      </div>
      <svg className="w-5 h-5 opacity-50 group-hover:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </button>
  );
}

// Verification status badge
interface VerificationBadgeProps {
  verified: boolean;
  platform: string;
  onVerify?: () => void;
}

export function VerificationBadge({ verified, platform, onVerify }: VerificationBadgeProps) {
  if (verified) {
    return (
      <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium">
        <CheckCircleIcon className="w-3 h-3" />
        Verified
      </div>
    );
  }

  return (
    <button
      onClick={onVerify}
      className="inline-flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-700 rounded-full text-xs font-medium hover:bg-amber-100 transition-colors"
    >
      <ExclamationCircleIcon className="w-3 h-3" />
      Verify for {platform}
    </button>
  );
}
